import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '../components/Field';
import { ViewHeader } from '../components/ViewHeader';
import { EntityRow } from '../components/EntityRow';
import { EmptyState } from '../components/EmptyState';
import { useWorkspace } from '../hooks/useWorkspace';
import { useLang } from '../hooks/useLang';
import { useEntityList } from '../hooks/useEntityList';
import { useToast } from '../components/Toast';
import { LIMITS } from '../../domain/constants';
import { newComp } from '../../domain/workspace';
import { t } from '../../i18n';
import type { WorkspaceStore } from '../../domain/store';
import type { Competition } from '../../types';

export function CompetitionsView({ store, hidden }: { store: WorkspaceStore; hidden: boolean }) {
  const workspace = useWorkspace(store);
  useLang();
  const toast = useToast();
  const { editing, startEditing, stopEditing, stopEditingIf } = useEntityList();
  const { comps } = workspace;

  function handleAdd(): void {
    const c = newComp();
    store.update((p) => p.comps.push(c));
    startEditing(c.id);
  }

  function handleDelete(id: string): void {
    const used = store.workspace.entries.filter((e) => e.compId === id).length;
    if (used > 0 && !confirm(t('competitions.deleteConfirm', { used }))) return;
    store.update((p) => {
      p.comps = p.comps.filter((c) => c.id !== id);
      // An Entry without its competition has nothing to be exported for —
      // drop it along with the competition rather than leave it dangling.
      p.entries = p.entries.filter((e) => e.compId !== id);
    });
    stopEditingIf(id);
    toast(t('competitions.deleted'));
  }

  return (
    <section className="mx-auto flex max-w-[1180px] flex-col gap-3 p-4" hidden={hidden}>
      <ViewHeader title={t('competitions.title')} lede={t('competitions.lede')} />

      {comps.length === 0 ? (
        <EmptyState title={t('competitions.empty.title')} text={t('competitions.empty.text')} />
      ) : (
        comps.map((c) =>
          c.id === editing ? (
            <CompForm key={c.id} c={c} store={store} onDone={stopEditing} onDelete={() => handleDelete(c.id)} />
          ) : (
            <EntityRow
              key={c.id}
              title={c.name || t('competitions.unnamed')}
              sub={`${c.date}${c.venue ? ` · ${c.venue}` : ''}`}
              actions={
                <>
                  <Button variant="outline" size="sm" onClick={() => startEditing(c.id)}>
                    {t('common.edit')}
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(c.id)}>
                    {t('common.delete')}
                  </Button>
                </>
              }
            />
          ),
        )
      )}
      <Button className="w-full" onClick={handleAdd}>
        {t('competitions.add')}
      </Button>
    </section>
  );
}

function CompForm({
  c,
  store,
  onDone,
  onDelete,
}: {
  c: Competition;
  store: WorkspaceStore;
  onDone: () => void;
  onDelete: () => void;
}) {
  return (
    <Card className="ring-2 ring-ring/40">
      <CardHeader>
        <CardTitle>{t('competitions.form.title')}</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          scope="comp"
          store={store}
          target={c}
          spec={{ label: t('competitions.field.name'), key: 'name', maxLength: LIMITS.competition, required: true, wide: true, placeholder: '2. German Masters 2026' }}
        />
        <Field
          scope="comp"
          store={store}
          target={c}
          spec={{ label: t('competitions.field.date'), key: 'date', type: 'date', required: true, hint: '' }}
        />
        <Field
          scope="comp"
          store={store}
          target={c}
          spec={{ label: t('competitions.field.austragungsort'), key: 'venue', maxLength: LIMITS.competition, required: true, placeholder: 'Ilsfeld, Schozachtalhalle' }}
        />
        <Field
          scope="comp"
          store={store}
          target={c}
          spec={{ label: t('competitions.field.organiser'), key: 'organiser', maxLength: LIMITS.competition, placeholder: 'RKV Ilsfeld' }}
        />
      </CardContent>
      <CardFooter>
        <Button onClick={onDone}>{t('common.done')}</Button>
        <Button variant="destructive" onClick={onDelete}>
          {t('common.delete')}
        </Button>
      </CardFooter>
    </Card>
  );
}
